import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, TrendingDown, Activity, DollarSign,
  RefreshCw, Settings, BookOpen, BarChart2, AlertCircle, CheckCircle, Clock
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Legend
} from 'recharts';
import apiService from '../../services/apiService';



// ─── Styled Components ───────────────────────────────────────
const fadeIn = keyframes`from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); }`;
const pulse = keyframes`0%, 100% { opacity: 1; } 50% { opacity: 0.4; }`;
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Page = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
  color: #e2e8f0;
  font-family: 'Inter', 'Pretendard', sans-serif;
  padding: 20px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 1px solid #1e2030;
`;

const BackBtn = styled.button`
  background: none; border: none; color: #64748b;
  cursor: pointer; padding: 8px; border-radius: 8px;
  transition: all 0.2s;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;

const TitleArea = styled.div`flex: 1;`;
const PageTitle = styled.h1`
  font-size: 22px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #10b981, #06b6d4);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const PageSub = styled.p`margin: 2px 0 0; font-size: 12px; color: #64748b;`;

const NavTabs = styled.div`
  display: flex; gap: 8px; margin-left: auto;
`;
const NavTab = styled.button`
  background: ${p => p.active ? 'rgba(16,185,129,0.15)' : 'transparent'};
  border: 1px solid ${p => p.active ? '#10b981' : '#1e2030'};
  color: ${p => p.active ? '#10b981' : '#64748b'};
  padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
  &:hover { border-color: #10b981; color: #10b981; }
`;

const StatusBadge = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12px; font-weight: 600;
  padding: 4px 10px; border-radius: 20px;
  background: ${p => p.active ? 'rgba(16,185,129,0.15)' : 'rgba(100,116,139,0.15)'};
  color: ${p => p.active ? '#10b981' : '#64748b'};
  border: 1px solid ${p => p.active ? '#10b981' : '#374151'};
`;
const PulseDot = styled.span`
  width: 7px; height: 7px; border-radius: 50%;
  background: ${p => p.active ? '#10b981' : '#64748b'};
  animation: ${p => p.active ? pulse : 'none'} 1.5s infinite;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 28px;
  animation: ${fadeIn} 0.4s ease;
`;

const StatCard = styled.div`
  background: #111827;
  border: 1px solid ${p => p.highlight ? p.color || '#10b981' : '#1e2030'};
  border-radius: 14px; padding: 18px 20px;
  transition: all 0.2s;
  box-shadow: ${p => p.highlight ? `0 0 20px ${p.color || '#10b981'}22` : 'none'};
`;
const StatLabel = styled.p`margin: 0 0 8px; font-size: 11px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;`;
const StatVal = styled.div`
  font-size: 24px; font-weight: 800;
  color: ${p => p.positive === true ? '#10b981' : p.positive === false ? '#ef4444' : '#e2e8f0'};
`;
const StatSub = styled.p`margin: 4px 0 0; font-size: 11px; color: #475569;`;

const ChartWrap = styled.div`
  background: #111827; border: 1px solid #1e2030;
  border-radius: 16px; padding: 24px; margin-bottom: 24px;
  animation: ${fadeIn} 0.5s ease;
`;
const ChartTitle = styled.h3`margin: 0 0 20px; font-size: 14px; font-weight: 700; color: #94a3b8;`;

const SignalList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
  margin-bottom: 28px;
  animation: ${fadeIn} 0.6s ease;
`;
const SignalCard = styled.div`
  background: #111827;
  border: 1px solid ${p =>
    p.signal === 'SYS2_ENTER' ? '#06b6d4' :
    p.signal === 'SYS1_ENTER' ? '#10b981' :
    p.signal === 'PYRAMID' ? '#f59e0b' :
    p.signal === 'STOP_LOSS' || p.signal === 'EXIT' ? '#ef4444' :
    '#1e2030'};
  border-radius: 12px; padding: 16px;
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); }
`;
const SignalBadge = styled.span`
  display: inline-block;
  font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 6px;
  background: ${p =>
    p.type === 'SYS2_ENTER' ? 'rgba(6,182,212,0.15)' :
    p.type === 'SYS1_ENTER' ? 'rgba(16,185,129,0.15)' :
    p.type === 'PYRAMID' ? 'rgba(245,158,11,0.15)' :
    p.type === 'STOP_LOSS' || p.type === 'EXIT' ? 'rgba(239,68,68,0.15)' :
    'rgba(100,116,139,0.15)'};
  color: ${p =>
    p.type === 'SYS2_ENTER' ? '#06b6d4' :
    p.type === 'SYS1_ENTER' ? '#10b981' :
    p.type === 'PYRAMID' ? '#f59e0b' :
    p.type === 'STOP_LOSS' || p.type === 'EXIT' ? '#ef4444' :
    '#64748b'};
`;

const ActionBar = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap;
  margin-bottom: 24px;
`;
const ActionBtn = styled.button`
  display: flex; align-items: center; gap: 7px;
  padding: 10px 18px; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.2s; border: none;
  background: ${p => p.danger ? 'rgba(239,68,68,0.1)' : p.primary ? 'rgba(16,185,129,0.15)' : '#1e2030'};
  color: ${p => p.danger ? '#ef4444' : p.primary ? '#10b981' : '#94a3b8'};
  border: 1px solid ${p => p.danger ? '#ef4444' : p.primary ? '#10b981' : '#374151'};
  &:hover { transform: translateY(-1px); opacity: 0.9; }
  &:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
`;

const SpinIcon = styled(RefreshCw)`
  animation: ${p => p.spinning ? css`${spin} 1s linear infinite` : 'none'};
`;

const SIGNAL_LABEL = {
  SYS2_ENTER: '🔵 SYS2 진입',
  SYS1_ENTER: '🟢 SYS1 진입',
  PYRAMID: '🟡 피라미딩',
  STOP_LOSS: '🔴 스탑로스',
  EXIT: '🟠 청산신호',
  HOLD: '⚪ 보유중',
  HOLD_MAX: '⚪ 보유(MAX)',
  NO_SIGNAL: '— 신호없음',
  SYS1_SKIP: '⏭ SYS1 스킵',
  NO_DATA: '❌ 데이터없음',
  ERROR: '❌ 오류',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e2030', border: '1px solid #374151', borderRadius: 8, padding: '10px 14px' }}>
      <p style={{ margin: '0 0 4px', fontSize: 11, color: '#64748b' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: '2px 0', fontSize: 13, fontWeight: 700, color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? `${p.value > 0 ? '+' : ''}${p.value.toFixed(2)}%` : p.value}
        </p>
      ))}
    </div>
  );
};

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.7); z-index: 10000;
  display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(4px);
`;
const ModalContent = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 16px;
  padding: 24px; width: 90%; max-width: 500px;
  animation: ${fadeIn} 0.2s ease;
  display: flex; flex-direction: column; max-height: 80vh;
`;
const ModalTitle = styled.h3`
  margin: 0 0 16px; font-size: 16px; color: #e2e8f0; display: flex; align-items: center; gap: 8px;
`;
const ModalBody = styled.div`
  flex: 1; overflow-y: auto; background: #0d1520; border-radius: 8px; padding: 12px;
  font-family: monospace; font-size: 12px; color: #94a3b8; line-height: 1.6;
  border: 1px solid #1e2030;
`;
const ModalBtn = styled.button`
  margin-top: 16px; padding: 12px; border-radius: 8px; font-weight: 600;
  background: #10b981; color: white; border: none; cursor: pointer;
  align-self: flex-end; width: 100%; transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
`;

// ─── Main Component ──────────────────────────────────────────
function TurtleDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [signals, setSignals] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [lastScreenerAt, setLastScreenerAt] = useState(null);
  const [lastScanAt, setLastScanAt] = useState(null);

  // Streaming State
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [scanStreamLogs, setScanStreamLogs] = useState([]);
  const [scanProgress, setScanProgress] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, perfRes, cfgRes] = await Promise.all([
        apiService.getTurtleStats(),
        apiService.getTurtlePerformance(),
        apiService.getTurtleConfig()
      ]);
      setStats(statsRes);
      setPerformance(perfRes);
      setConfig(cfgRes);
      
      // Update last run times from config/stats
      if (cfgRes.lastScreenerAt) setLastScreenerAt(cfgRes.lastScreenerAt);
      if (cfgRes.lastScanAt) setLastScanAt(cfgRes.lastScanAt);
      
    } catch (err) {
      console.error('loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Batch Watcher Logic (Frontend-driven) ───────────────────
  useEffect(() => {
    if (!config?.isActive && !config?.useAutoScreener) return;

    const interval = setInterval(async () => {
      const now = new Date();
      const day = now.getDay();
      if (day === 0 || day === 6) return; // Skip weekends

      const timeStr = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');
      const todayStr = now.toISOString().split('T')[0];

      // 1. Morning Screener (08:00)
      if (config.useAutoScreener && timeStr >= '08:00' && timeStr < '08:10') {
        const lastDate = lastScreenerAt?.split('T')[0];
        if (lastDate !== todayStr && !running) {
          console.log('[BatchWatcher] 🐢 Triggering Turtle Morning Screener...');
          try {
            await apiService.runTurtleScreener();
            const newCfg = await apiService.getTurtleConfig();
            setLastScreenerAt(newCfg.lastScreenerAt);
            alert('✅ [자동] 터틀 모닝 스크리너가 완료되었습니다.');
          } catch (e) {
            console.error('Auto Screener Error:', e);
          }
        }
      }

      // 2. Afternoon Signal Scan (15:20)
      if (config.isActive && timeStr >= '15:20' && timeStr < '15:35') {
        const lastDate = lastScanAt?.split('T')[0];
        if (lastDate !== todayStr && !running) {
          console.log('[BatchWatcher] 🐢 Triggering Turtle Signal Scan...');
          try {
            await apiService.runTurtleScan();
            const newCfg = await apiService.getTurtleConfig();
            setLastScanAt(newCfg.lastScanAt);
            await loadData();
            alert('✅ [자동] 터틀 장마감 신호 스캔이 완료되었습니다.');
          } catch (e) {
            console.error('Auto Scan Error:', e);
          }
        }
      }
    }, 30000); // Check every 30s

    return () => clearInterval(interval);
  }, [config, lastScreenerAt, lastScanAt, running, loadData]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRun = async () => {
    setIsScanModalOpen(true);
    setRunning(true);
    setScanStreamLogs(['[SYSTEM] 수동 신호 스캔 시작...']);
    setScanProgress({ current: 0, total: 100, status: '초기화 중' });

    try {
      await apiService.runTurtleScanStream((data) => {
        if (data.type === 'start') {
          setScanProgress(p => ({ ...p, total: data.total }));
        } else if (data.type === 'progress') {
          setScanProgress(p => ({ ...p, current: data.current, status: `스캔 중: ${data.name} (${data.symbol})` }));
        } else if (data.type === 'hit') {
          setScanStreamLogs(prev => [...prev, `[${data.signal}] ${data.name}(${data.symbol}): ${data.detail}`]);
        } else if (data.type === 'done') {
          setScanProgress(p => ({ ...p, status: '스캔 완료' }));
          setScanStreamLogs(prev => [...prev, '[SYSTEM] 전체 스캔 작업이 완료되었습니다.']);
          setLastScan(data.summary);
          loadData(); // 결과 갱신
        } else if (data.type === 'error') {
          setScanStreamLogs(prev => [...prev, `[ERROR] ${data.error}`]);
        }
      });
    } catch (err) {
      setScanStreamLogs(prev => [...prev, `[ERROR] 스트리밍 오류: ${err.message}`]);
    } finally {
      setRunning(false);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await apiService.getTurtleSignalPreview();
      setSignals(res);
    } catch (err) {
      alert('미리보기 오류: ' + err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatNum = (n) => n != null ? Number(n).toLocaleString() : '-';
  const formatPct = (n) => n != null ? `${n > 0 ? '+' : ''}${Number(n).toFixed(2)}%` : '-';

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>🐢 Turtle Trading</PageTitle>
          <PageSub>Richard Dennis 규칙 기반 가상 자동매매 | System1(20일) + System2(55일)</PageSub>
        </TitleArea>
        <StatusBadge active={config?.isActive}>
          <PulseDot active={config?.isActive} />
          {config?.isActive ? '자동매매 실행 중' : '비활성'}
        </StatusBadge>
        <NavTabs>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginRight: '12px' }}>
            {lastScreenerAt && (
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                최근 스크리닝: {new Date(lastScreenerAt).toLocaleString()}
              </div>
            )}
            {lastScanAt && (
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                최근 신호스캔: {new Date(lastScanAt).toLocaleString()}
              </div>
            )}
          </div>
          <NavTab active onClick={() => navigate('/labs/turtle')}>대시보드</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/positions')}>포지션</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/journal')}>거래일지</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/multi-sim')}>멀티 시뮬</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/screener')}>스크리너</NavTab>
          <NavTab onClick={() => navigate('/labs/turtle/settings')}><Settings size={13} /></NavTab>
        </NavTabs>
      </TopBar>

      <ActionBar>
        <ActionBtn primary onClick={handleRun} disabled={running}>
          <SpinIcon size={14} spinning={running ? 1 : 0} />
          {running ? '스캔 중...' : '수동 신호 스캔'}
        </ActionBtn>
        <ActionBtn onClick={handlePreview} disabled={previewLoading}>
          <Activity size={14} />
          {previewLoading ? '조회 중...' : '현재 신호 미리보기'}
        </ActionBtn>
        <ActionBtn onClick={() => navigate('/labs/turtle/screener')}>
          <Activity size={14} />
          터틀 스크리너
        </ActionBtn>
        <ActionBtn onClick={() => navigate('/labs/turtle/positions')}>
          <BarChart2 size={14} />
          포지션 현황
        </ActionBtn>
        <ActionBtn onClick={() => navigate('/labs/turtle/journal')}>
          <BookOpen size={14} />
          거래 일지
        </ActionBtn>
      </ActionBar>

      {/* 핵심 통계 카드 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#475569' }}>데이터 로딩 중...</div>
      ) : (
        <>
          <StatGrid>
            <StatCard highlight color="#10b981">
              <StatLabel>💰 총 평가금액</StatLabel>
              <StatVal>{formatNum(stats?.totalEquity)}원</StatVal>
              <StatSub>초기: {formatNum(stats?.initialEquity)}원</StatSub>
            </StatCard>
            <StatCard highlight={stats?.cumulativeReturn > 0} color={stats?.cumulativeReturn > 0 ? '#10b981' : '#ef4444'}>
              <StatLabel>📈 누적 수익률</StatLabel>
              <StatVal positive={stats?.cumulativeReturn > 0 ? true : stats?.cumulativeReturn < 0 ? false : undefined}>
                {formatPct(stats?.cumulativeReturn)}
              </StatVal>
              <StatSub>총 손익: {formatNum(stats?.totalPnl)}원</StatSub>
            </StatCard>
            <StatCard>
              <StatLabel>🏆 승률</StatLabel>
              <StatVal positive={parseFloat(stats?.winRate) >= 50 ? true : false}>
                {stats?.winRate}%
              </StatVal>
              <StatSub>{stats?.wins}승 {stats?.losses}패 (총 {stats?.totalTrades}건)</StatSub>
            </StatCard>
            <StatCard>
              <StatLabel>📉 최대 낙폭 (MDD)</StatLabel>
              <StatVal positive={false}>-{stats?.maxDrawdown}%</StatVal>
              <StatSub>손익비: {stats?.profitFactor}x</StatSub>
            </StatCard>
            <StatCard>
              <StatLabel>🎯 평균 수익/손실</StatLabel>
              <StatVal style={{ fontSize: 18 }}>
                <span style={{ color: '#10b981' }}>+{stats?.avgWinRate}%</span>
                {' / '}
                <span style={{ color: '#ef4444' }}>-{stats?.avgLossRate}%</span>
              </StatVal>
              <StatSub>SYS1: {stats?.sys1Trades}건 | SYS2: {stats?.sys2Trades}건</StatSub>
            </StatCard>
            <StatCard>
              <StatLabel>📊 오픈 포지션</StatLabel>
              <StatVal>{performance[performance.length - 1]?.openPositionsCount || 0}개</StatVal>
              <StatSub>감시종목: {config?.watchlist?.length || 0}개</StatSub>
            </StatCard>
          </StatGrid>

          {/* 수익률 차트 */}
          {performance.length > 0 && (
            <ChartWrap>
              <ChartTitle>📊 누적 수익률 곡선</ChartTitle>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={performance} margin={{ left: -10, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2030" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#475569' }}
                    tickFormatter={v => v?.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: '#475569' }}
                    tickFormatter={v => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`} />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={0} stroke="#374151" strokeDasharray="4 4" />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#64748b' }} />
                  <Line type="monotone" dataKey="cumulativeReturn" name="누적 수익률"
                    stroke="#10b981" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                  <Line type="monotone" dataKey="dailyReturn" name="일별 수익률"
                    stroke="#06b6d4" strokeWidth={1} dot={false} strokeDasharray="4 2" />
                </LineChart>
              </ResponsiveContainer>
            </ChartWrap>
          )}

          {/* 신호 미리보기 */}
          {signals && (
            <>
              <ChartTitle style={{ marginBottom: 14, color: '#94a3b8', fontSize: 14 }}>
                🔍 현재 신호 미리보기 ({signals.date})
              </ChartTitle>
              <SignalList>
                {signals.signals.map(s => (
                  <SignalCard key={s.symbol} signal={s.signal}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.symbol}</div>
                      </div>
                      <SignalBadge type={s.signal}>{SIGNAL_LABEL[s.signal] || s.signal}</SignalBadge>
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>
                      <span>현재가: <b style={{ color: '#e2e8f0' }}>{formatNum(s.todayClose)}원</b></span>
                      {s.N && <span style={{ marginLeft: 10 }}>N: <b style={{ color: '#f59e0b' }}>{formatNum(s.N)}</b></span>}
                    </div>
                    {s.details && (
                      <div style={{ fontSize: 11, color: '#475569', marginTop: 6, lineHeight: 1.6 }}>
                        {s.signal === 'NO_SIGNAL' && s.details.distToHigh20 && (
                          <>20일 고가까지: <b style={{ color: '#64748b' }}>{s.details.distToHigh20}</b></>
                        )}
                        {s.details.currentUnits != null && (
                          <>Unit: {s.details.currentUnits}/{config?.maxUnitsPerMarket || 4} | SL: {formatNum(s.details.stopLoss)}</>
                        )}
                      </div>
                    )}
                  </SignalCard>
                ))}
              </SignalList>
            </>
          )}

          {/* 마지막 스캔 로그 */}
          {lastScan?.log && (
            <ChartWrap>
              <ChartTitle>📋 마지막 스캔 로그</ChartTitle>
              <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b', lineHeight: 1.8 }}>
                {lastScan.log.map((line, i) => (
                  <div key={i} style={{
                    color: line.includes('[ENTER]') ? '#10b981' :
                           line.includes('[STOP]') || line.includes('[ERROR]') ? '#ef4444' :
                           line.includes('[PYRAMID]') ? '#f59e0b' :
                           line.includes('[EXIT]') ? '#f97316' :
                           '#64748b'
                  }}>{line}</div>
                ))}
              </div>
            </ChartWrap>
          )}
        </>
      )}

      {/* 실시간 스캔 모달 */}
      {isScanModalOpen && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>
              <SpinIcon size={18} spinning={running ? 1 : 0} style={{ color: running ? '#0ea5e9' : '#10b981' }} />
              수동 신호 스캔 {running ? '진행 중' : '완료'}
            </ModalTitle>
            
            <div style={{ marginBottom: 12, fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
              <span>{scanProgress?.status}</span>
              <span>{scanProgress?.current || 0} / {scanProgress?.total || 100}</span>
            </div>
            
            <div style={{ background: '#1e2030', height: 6, borderRadius: 3, marginBottom: 16, overflow: 'hidden' }}>
              <div style={{ 
                height: '100%', 
                background: running ? '#0ea5e9' : '#10b981', 
                width: `${scanProgress?.total ? (scanProgress.current / scanProgress.total) * 100 : 0}%`,
                transition: 'width 0.2s',
              }} />
            </div>

            <ModalBody>
              {scanStreamLogs.map((log, i) => (
                <div key={i} style={{
                  color: log.includes('ENTER_SYS') ? '#10b981' :
                         (log.includes('STOP_LOSS') || log.includes('ERROR')) ? '#ef4444' :
                         log.includes('PYRAMID') ? '#f59e0b' :
                         log.includes('EXIT') ? '#f97316' :
                         log.includes('SYS1_SKIP') ? '#a855f7' :
                         '#64748b'
                }}>
                  {log}
                </div>
              ))}
              {running && scanStreamLogs.length > 0 && <div style={{ color: '#64748b', marginTop: 8 }}>...</div>}
            </ModalBody>
            
            {!running && (
              <ModalBtn onClick={() => setIsScanModalOpen(false)}>닫기</ModalBtn>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

    </Page>
  );
}

export default TurtleDashboardPage;
